import { useEffect, useState, useMemo } from "react";
import Pagination from "@/components/dashboard/Pagination";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, MoreHorizontal, Edit, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface Employee {
  id: string;
  name: string;
  image_url: string | null;
  active: boolean;
  sort_order: number;
}

const EmployeesPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return employees.slice(start, start + PAGE_SIZE);
  }, [employees, page]);

  const fetchEmployees = async () => {
    if (!profile?.organisation_id) return;
    const { data } = await supabase
      .from("employees")
      .select("*")
      .eq("organisation_id", profile.organisation_id)
      .order("sort_order", { ascending: true });
    setEmployees((data as any) || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchEmployees();
  }, [profile?.organisation_id]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("employees").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(`${deleteTarget.name} ${t("employees.deleted")}`);
      setEmployees((prev) => prev.filter((e) => e.id !== deleteTarget.id));
    }
    setDeleteTarget(null);
  };

  const onNavigate = (id: string) => navigate(`/dashboard/employees/${id}/edit`);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-foreground">{t("employees.title")}</h1>
        <Button asChild>
          <Link to="/dashboard/employees/new">
            <Plus className="h-4 w-4 mr-2" />
            {t("employees.add")}
          </Link>
        </Button>
      </div>

      {employees.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <p className="text-muted-foreground mb-4">{t("employees.no_items")}</p>
          <Button asChild>
            <Link to="/dashboard/employees/new">
              <Plus className="h-4 w-4 mr-2" />
              {t("employees.add")}
            </Link>
          </Button>
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div className="hidden md:block rounded-xl border border-border bg-card overflow-hidden">
            <div className="grid grid-cols-[3rem_1fr_5rem_3rem] items-center gap-2 px-4 py-3 border-b border-border bg-muted/50">
              <div></div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("employees.col_name")}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("employees.col_status")}</span>
              <span></span>
            </div>
            {paginated.map((emp) => (
              <div
                key={emp.id}
                className="grid grid-cols-[3rem_1fr_5rem_3rem] items-center gap-2 px-4 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => onNavigate(emp.id)}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={emp.image_url || undefined} alt={emp.name} />
                  <AvatarFallback className="text-xs">{emp.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <p className="text-sm font-medium text-foreground truncate">{emp.name}</p>
                <Badge variant={emp.active ? "default" : "secondary"} className="text-[10px] w-fit">
                  {emp.active ? t("employees.active") : t("employees.inactive")}
                </Badge>
                <div className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/dashboard/employees/${emp.id}/edit`} className="flex items-center">
                          <Edit className="h-4 w-4 mr-2" /> {t("employees.edit")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(emp); }}>
                        <Trash2 className="h-4 w-4 mr-2" /> {t("employees.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile */}
          <div className="md:hidden space-y-3">
            {paginated.map((emp) => (
              <div key={emp.id} className="rounded-xl border border-border bg-card shadow-card p-4 cursor-pointer transition-colors" onClick={() => onNavigate(emp.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={emp.image_url || undefined} alt={emp.name} />
                      <AvatarFallback className="text-xs">{emp.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">{emp.name}</h3>
                      <Badge variant={emp.active ? "default" : "secondary"} className="text-[10px] mt-1">
                        {emp.active ? t("employees.active") : t("employees.inactive")}
                      </Badge>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/dashboard/employees/${emp.id}/edit`} className="flex items-center">
                          <Edit className="h-4 w-4 mr-2" /> {t("employees.edit")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(emp); }}>
                        <Trash2 className="h-4 w-4 mr-2" /> {t("employees.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>

          <Pagination currentPage={page} totalItems={employees.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("employees.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("employees.delete_desc_pre")} <span className="font-medium text-foreground">{deleteTarget?.name}</span>{t("employees.delete_desc_post")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("employees.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("employees.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmployeesPage;
